import {
    Box,
    Checkbox,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { formatDistance } from 'date-fns';
import {
    RecordContextProvider,
    SimpleListLoading,
    useListContext,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { MemberId, MissionLight } from '@wwf/sdk';
import { MissionStatusChip } from '../chips/MissionStatusChip';
import { MissionTypeChip } from '../chips/MissionTypeChip';
import { MissionDateChip } from '../chips/MissionDateChip';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { MissionEmpty } from './MissionEmpty';
import { getLinkToMission } from '../getLinkToMission';
import { MediaIcon } from '@app/media/MediaIcon';
import { MemberIcon } from '@app/member/MemberIcon';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { ReportIcon } from '../ReportIcon';
import { SessionCount } from '../chips/SessionCount';
import { MissionVisibilityIcon } from '../MissionVisibilityIcon';

export const MissionListContent = ({
    noBulkActions,
    memberId,
}: {
    noBulkActions?: boolean;
    memberId?: MemberId;
}) => {
    const navigate = useNavigate();
    const {
        data: mission,
        error,
        isPending,
        onToggleItem,
        selectedIds,
    } = useListContext<MissionLight>();

    if (isPending) {
        return <SimpleListLoading hasLeftAvatarOrIcon hasSecondaryText />;
    }
    if (error) {
        return null;
    }
    const now = Date.now();

    if (!mission?.length) return <MissionEmpty />;

    return (
        <List>
            {mission.map(mission => (
                <RecordContextProvider key={mission.id} value={mission}>
                    <ListItem
                        disablePadding
                        sx={theme =>
                            memberId != null &&
                            mission.contact?.id != null &&
                            memberId === mission.contact.id
                                ? {
                                      bgcolor: alpha(
                                          theme.palette.success.main,
                                          0.05
                                      ),
                                  }
                                : {}
                        }
                    >
                        <Stack flex={1}>
                            <ListItemButton
                                onClick={() =>
                                    navigate(getLinkToMission(mission.id))
                                }
                            >
                                {!noBulkActions && (
                                    <ListItemIcon sx={{ minWidth: '2.5em' }}>
                                        <Checkbox
                                            edge="start"
                                            checked={selectedIds.includes(
                                                mission.id
                                            )}
                                            tabIndex={-1}
                                            disableRipple
                                            onClick={e => {
                                                e.stopPropagation();
                                                onToggleItem(mission.id);
                                            }}
                                        />
                                    </ListItemIcon>
                                )}
                                <Stack
                                    direction="column"
                                    gap={0.2}
                                    alignContent="center"
                                >
                                    <ListItemText
                                        sx={{
                                            marginRight: 20,
                                        }}
                                        primary={mission.name}
                                    />

                                    <Stack direction="row" gap={1}>
                                        <MissionStatusChip
                                            status={mission.status}
                                        />
                                        <MissionTypeChip type={mission.type} />
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        gap={1}
                                        alignItems="center"
                                    >
                                        <Box
                                            component="span"
                                            onClick={e => e.stopPropagation()}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <AntennaLightCard
                                                record={
                                                    mission.antenna ?? undefined
                                                }
                                            />
                                        </Box>
                                        <Box
                                            component="span"
                                            onClick={e => e.stopPropagation()}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <MemberLightCard
                                                record={
                                                    mission.contact ?? undefined
                                                }
                                            />
                                        </Box>

                                        {(mission.nbMedias ?? 0) > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.2,
                                                }}
                                            >
                                                <Typography variant="subtitle2">
                                                    {mission.nbMedias}
                                                </Typography>
                                                <MediaIcon />
                                            </Box>
                                        )}
                                        {(mission.totalTouchedPersonCount ??
                                            0) > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.2,
                                                }}
                                            >
                                                <Typography variant="subtitle2">
                                                    {
                                                        mission.totalTouchedPersonCount
                                                    }
                                                </Typography>
                                                <PeopleOutlineIcon />
                                            </Box>
                                        )}

                                        {mission.reportSentDate ? (
                                            <ReportIcon />
                                        ) : null}
                                    </Stack>
                                </Stack>
                                <ListItemSecondaryAction>
                                    <Stack
                                        direction="column"
                                        gap={1}
                                        alignItems="flex-end"
                                        justifyContent="flex-start"
                                    >
                                        <MissionDateChip
                                            date={mission.startDate}
                                        />
                                        <Stack direction="row" gap={1}>
                                            {mission.whoCanSee && (
                                                <MissionVisibilityIcon
                                                    visibility={
                                                        mission.whoCanSee
                                                    }
                                                />
                                            )}

                                            <SessionCount
                                                count={mission.nbSessions ?? 0}
                                            />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.2,
                                                }}
                                            >
                                                <Typography variant="subtitle2">
                                                    {mission.participantIds
                                                        ?.length ?? 0}
                                                </Typography>
                                                <MemberIcon />
                                            </Box>
                                        </Stack>
                                        {/* TODO : fix this
                                            <MissionMemberParticipationSessionsChip
                                            sessions={mission.sessions}
                                            memberId={memberId}
                                        /> */}
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            component="span"
                                        >
                                            {'last activity '}
                                            {formatDistance(
                                                mission.updatedAt,
                                                now
                                            ) + ' ago'}
                                        </Typography>
                                    </Stack>
                                </ListItemSecondaryAction>
                            </ListItemButton>
                            <Divider variant="middle" flexItem />
                        </Stack>
                    </ListItem>
                </RecordContextProvider>
            ))}

            {mission.length === 0 && (
                <ListItem>
                    <ListItemText primary="No mission found" />
                </ListItem>
            )}
        </List>
    );
};
